import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {isNil} from 'lodash'
import {useCallback, useEffect, useState} from 'react'

interface UseFileOptions<T> {
  enabled?: boolean
  select?: (data: ArrayBuffer) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useFile = <T = ArrayBuffer>(
  url: string,
  contentType: string,
  fileId?: string | number,
  options?: UseFileOptions<T>
) => {
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const fetchFile = useCallback(async () => {
    if (!url) return {data: undefined}

    setIsLoading(true)
    setIsFetching(true)

    try {
      const absUrl = `/documents/${url}`

      const response = await ApiClient.axiosInstance.get<ArrayBuffer>(absUrl, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': contentType,
          Accept: contentType
        }
      })

      const result = select ? select(response.data) : (response.data as T)

      setData(result)
      setError(null)
      onSuccess?.(result)

      return {data: result}
    } catch (err) {
      if (axios.isAxiosError(err)) {
        console.error(`Error fetching fileId=${fileId}:`, err)
        setError(err)
        onError?.(err)
      } else {
        console.error('Unexpected error:', err)
      }
      return {data: undefined}
    } finally {
      setIsLoading(false)
      setIsFetching(false)
    }
  }, [url, contentType, fileId, select, onSuccess, onError])

  useEffect(() => {
    if (enabled && url && !isNil(fileId)) {
      void fetchFile()
    }
  }, [enabled, fetchFile, url, fileId])

  return {
    data,
    error,
    isError: !!error,
    isLoading,
    isFetching,
    refetch: fetchFile
  }
}
